import React, { useMemo } from 'react'
import { WidgetId } from 'features/types'
import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
  ChevronRight as ChevronRightIcon,
  ChevronDown as ChevronDownIcon
  // @ts-ignore
} from '@netbasequid/core'
import styled from 'styled-components'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
import { MessageConst } from 'enums'
import { useDisplayTheme } from 'hooks/useUiState'
import CountType from 'enums/CountType'
import ViewOption from 'enums/InsightViewOption'
import ExpandedTable from 'components/visualization/Table/ExpandedTable'
import { simpleNumberFormatter } from 'utils/numberFormatterUtil'
import TableSortOption from 'enums/TableSortOption'

import StarRating from './StarRating'
import SurveyScore from './SurveyScore'
import useTagsData from '../../useTagsData'
import { ExtendedInsightData } from '../../types'

const IndicatorContainer = styled.div`
  padding-top: 3px;
  cursor: pointer;
`

const NameContainer = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`

const NameCell = styled.div<{ depth: number }>`
  display: flex;
  gap: 8px;
  align-items: center;
  padding-left: ${({ depth }) => `${depth * 24}px`};
`

interface TagsTableProps {
  widgetId: WidgetId
  onRowClick?: (row: ExtendedInsightData) => void
}

const TagsTable = ({ widgetId, onRowClick }: TagsTableProps) => {
  const {
    tableData,
    maxValue,
    countType,
    viewOption,
    showStarRating,
    showSurveyScore,
    expandableTableSortOptions
  } = useTagsData(widgetId)
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<ExtendedInsightData>[]>(() => {
    const columnArr: ColumnDef<ExtendedInsightData>[] = [
      {
        accessorKey: 'name',
        id: 'name',
        header: () =>
          viewOption === ViewOption.TAGS ? t('tag_other') : t('productHierarchy_other'),
        cell: ({ row, getValue }) => (
          <NameCell depth={row.depth}>
            {row.getCanExpand() && (
              <IndicatorContainer
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  row.getToggleExpandedHandler()()
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    row.getToggleExpandedHandler()()
                  }
                }}>
                {row.getIsExpanded() ? (
                  <ChevronDownIcon mode={mode} fontSize={14} />
                ) : (
                  <ChevronRightIcon mode={mode} fontSize={14} />
                )}
              </IndicatorContainer>
            )}
            <NameContainer>{getValue<boolean>()}</NameContainer>
          </NameCell>
        ),
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.original.name!
          const valueB = rowB.original.name!
          return valueA < valueB ? -1 : 1
        },
        meta: {
          cellProps: {
            contentStyle: {
              justifyContent: 'flex-start'
            }
          }
        }
      },
      {
        accessorKey: 'value',
        id: 'value',
        header: () => (countType === CountType.POSTS ? t('post_other') : t('mention_other')),
        sortingFn: 'basic',
        meta: {
          cells: [
            ({ getValue }) => {
              const cellValue = getValue<number>()
              return simpleNumberFormatter(cellValue)
            },
            ({ getValue }) => {
              const cellValue = getValue<number>()
              return (
                <SimpleBar
                  mode={mode}
                  progress={(cellValue / maxValue) * 100}
                  colorSetting={
                    countType === CountType.POSTS
                      ? 'metric.mainMetrics.post'
                      : 'metric.mainMetrics.mentions'
                  }
                  startPoint={0}
                  displayMinProgress
                  adjustLowProgress
                />
              )
            }
          ],
          cellsProps: [
            {
              fitWidth: true,
              style: { padding: '0 4px 0 12px' }
            },
            {
              style: { padding: '0 12px 0 4px' }
            }
          ]
        }
      }
    ]
    if (showStarRating) {
      columnArr.push({
        accessorKey: 'overallStarRatingsAverage',
        id: 'overallStarRatingsAverage',
        header: () => t('overallStarRating_other'),
        sortingFn: 'basic',
        meta: {
          cells: [
            ({ getValue }) => {
              const cellValue = getValue<number>()
              return cellValue < 0 ? MessageConst.EM_DASH : cellValue
            },
            ({ getValue }) => {
              const cellValue = getValue<number>()
              return <StarRating value={cellValue} maxValue={5} />
            }
          ],
          cellsProps: [
            {
              fitWidth: true,
              style: { padding: '0 4px 0 12px' }
            },
            {
              style: { padding: '0 12px 0 4px' },
              contentStyle: { justifyContent: 'flex-start' }
            }
          ]
        }
      })
    }
    if (showSurveyScore) {
      columnArr.push({
        accessorKey: 'overallSurveyScoresAverage',
        id: 'overallSurveyScoresAverage',
        header: () => t('overallSurveyScore_other'),
        sortingFn: 'basic',
        meta: {
          cells: [
            ({ getValue }) => {
              const cellValue = getValue<number>()
              return cellValue < 0 ? MessageConst.EM_DASH : cellValue
            },
            ({ getValue }) => {
              const cellValue = getValue<number>()
              return <SurveyScore value={cellValue} maxValue={10} />
            }
          ],
          cellsProps: [
            {
              fitWidth: true,
              style: { padding: '0 4px 0 12px' }
            },
            {
              style: { padding: '0 12px 0 4px' },
              contentStyle: { justifyContent: 'flex-start' }
            }
          ]
        }
      })
    }
    return columnArr
  }, [maxValue, countType, viewOption, mode, showSurveyScore, showStarRating, t])

  const defaultSorting = useMemo(() => {
    const sortIndex = expandableTableSortOptions.findIndex(
      (sortOption) => sortOption !== TableSortOption.NONE
    )

    if (sortIndex === -1) {
      return {
        id: 'value',
        desc: true
      }
    }

    return {
      id: columns[sortIndex].id!,
      desc: expandableTableSortOptions[sortIndex] === TableSortOption.DESCEND
    }
  }, [expandableTableSortOptions, columns])

  return (
    <ExpandedTable
      data={tableData}
      columns={columns}
      getSubRows={(row) => row.subRows!}
      initialState={{
        expanded: true,
        sorting: [defaultSorting]
      }}
      onRowClick={onRowClick}
    />
  )
}

export default TagsTable
